import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusBadge(s) {
  if (s === 'accepted') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s === 'rejected') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

function statusLabel(s) {
  if (s === 'accepted') return 'Accepted';
  if (s === 'rejected') return 'Rejected';
  return 'Pending';
}

export default function SubmTable({ entities, pkField, nameField, allFields, onSelect, onApprove, onReject }) {
  var columns = [];
  if (pkField) columns.push(pkField);
  if (nameField && nameField !== pkField) columns.push(nameField);
  for (var i = 0; i < allFields.length && columns.length < 6; i++) {
    if (columns.indexOf(allFields[i]) === -1) columns.push(allFields[i]);
  }

  if (!entities || entities.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground" data-testid="subm-empty-table">
        <p className="text-sm">No entities found matching your filters.</p>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="subm-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {columns.map(function(col) {
                return <th key={col} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">{col}</th>;
              })}
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Last Submitted</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <TableBody entities={entities} columns={columns} onSelect={onSelect} onApprove={onApprove} onReject={onReject} />
        </table>
      </div>
    </div>
  );
}

function TableBody({ entities, columns, onSelect, onApprove, onReject }) {
  return (
    <tbody>
      {entities.map(function(ent) {
        var latestSub = ent.submissions && ent.submissions[0];
        var payload = latestSub ? latestSub.raw_payload || {} : {};
        var approval = latestSub ? latestSub.approval_status || 'pending' : 'pending';
        var subId = latestSub ? latestSub.id : null;

        return (
          <tr
            key={ent.primary_key}
            className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer transition-colors duration-100"
            onClick={function() { onSelect(ent); }}
            data-testid={'table-row-' + ent.primary_key}
          >
            {columns.map(function(col) {
              var val = payload[col];
              return <td key={col} className="px-4 py-3 max-w-[200px] truncate">{val != null ? String(val) : '-'}</td>;
            })}
            <td className="px-4 py-3">
              <Badge className={'text-xs border ' + statusBadge(approval)} variant="outline">{statusLabel(approval)}</Badge>
            </td>
            <td className="px-4 py-3 text-muted-foreground">{formatDate(ent.latest_date)}</td>
            <td className="px-4 py-3" onClick={function(e) { e.stopPropagation(); }}>
              {approval === 'pending' && subId && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-50" onClick={function() { onApprove(subId); }} data-testid={'approve-row-' + ent.primary_key}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-700 hover:bg-red-50" onClick={function() { onReject(subId); }} data-testid={'reject-row-' + ent.primary_key}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}
